import { useState, useCallback, useMemo, useEffect } from 'react';
import { QueryGroup, QueryRule, QueryField } from '../../types/QueryBuilder';
import { generateId, isRuleValid } from '../../utils/queryBuilderUtils';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';

const SIMPLE_GROUP_ID = 'simple-group';

const createRuleForField = (field: QueryField): QueryRule => ({
    id: generateId(),
    field: field.name,
    operator: (field.defaultOperator as QueryRule['operator']) || '=',
    value: '',
});

export const useSimpleQueryBuilder = () => {
    const { simpleFields, loadingFields, errorFields } = useQueryBuilderData();

    const [rules, setRules] = useState<QueryRule[]>([]);

    useEffect(() => {
        setRules(prevRules =>
            simpleFields.map(field =>
                prevRules.find(rule => rule.field === field.name) || createRuleForField(field)
            )
        );
    }, [simpleFields]);

    const updateField = useCallback((fieldName: string, data: Partial<QueryRule>) => {
        setRules(prevRules =>
            prevRules.map(rule => (rule.field === fieldName ? { ...rule, ...data } : rule))
        );
    }, []);

    const resetFields = useCallback(() => {
        setRules(simpleFields.map(createRuleForField));
    }, [simpleFields]);

    const group = useMemo<QueryGroup>(() => ({
        id: SIMPLE_GROUP_ID,
        combinator: 'AND',
        rules
    }), [rules]);

    const groups = useMemo(() => [group], [group]);

    const isValid = useMemo(() => rules.some(isRuleValid), [rules]);

    return {
        simpleFields,
        rules,
        group,
        groups,
        isValid,
        updateField,
        resetFields,
        loadingFields,
        fieldsError: errorFields
    };
};
